import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilmStrip, MagnifyingGlass, Eye, EyeSlash, PencilSimple, Trash, Check, X, Play, Image } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AllVideosPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [contents, setContents] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);

  useEffect(() => {
    axios.get(`${API}/projects`, { withCredentials: true }).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const fetchContents = useCallback(async (page = 1) => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      let url = `${API}/projects/${selectedProject}/search?page=${page}&limit=25`;
      if (search) url += `&q=${encodeURIComponent(search)}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setContents(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedProject, search, filterStatus]);

  useEffect(() => { if (selectedProject) fetchContents(1); }, [selectedProject, fetchContents]);

  const toggleStatus = async (item) => {
    const newStatus = item.status === "published" ? "draft" : "published";
    const endpoint = newStatus === "published" ? "publish" : "draft";
    try {
      await axios.post(`${API}/projects/${item.project_id}/contents/${item.id}/${endpoint}`, {}, { withCredentials: true });
      toast.success(`${newStatus === "published" ? "Activated" : "Deactivated"}`);
      fetchContents(contents.page);
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title.substring(0, 50)}..."?`)) return;
    try {
      await axios.delete(`${API}/projects/${item.project_id}/contents/${item.id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchContents(contents.page);
    } catch { toast.error("Failed"); }
  };

  const openEdit = (item) => {
    setEditItem({ ...item, tags_str: (item.tags || []).join(", "), categories_str: (item.categories || []).join(", ") });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      const payload = {
        title: editItem.title,
        description: editItem.description,
        thumbnail_url: editItem.thumbnail_url,
        duration: editItem.duration,
        tags: editItem.tags_str ? editItem.tags_str.split(",").map(t => t.trim()) : [],
        categories: editItem.categories_str ? editItem.categories_str.split(",").map(c => c.trim()) : [],
      };
      await axios.put(`${API}/projects/${editItem.project_id}/contents/${editItem.id}`, payload, { withCredentials: true });
      toast.success("Updated");
      setEditDialog(false);
      fetchContents(contents.page);
    } catch { toast.error("Failed to update"); }
  };

  const hasImage = (item) => item.thumbnail_url && !item.thumbnail_url.startsWith("data:");

  return (
    <div data-testid="all-videos-page" className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>All Videos</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Manage all video content across projects</p>
      </div>

      {/* Filters */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm p-4 flex flex-wrap gap-3 items-end">
        <div className="w-52">
          <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Project</label>
          <Select value={selectedProject} onValueChange={v => { setSelectedProject(v); setContents({ items: [], total: 0, page: 1, pages: 1 }); }}>
            <SelectTrigger data-testid="videos-project-select" className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent className="bg-[#141417] border-[#2A2A2E] text-white">
              {projects.map(p => <SelectItem key={p.id} value={p.id} className="hover:bg-[#1E1E22]">{p.name} ({p.contents_count})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#555]" />
            <Input data-testid="videos-search" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchContents(1)} placeholder="Search title..." className="pl-8 bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs" />
          </div>
        </div>
        <div className="w-36">
          <label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Status</label>
          <select data-testid="videos-status-filter" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm h-9 text-xs px-2">
            <option value="">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <Button data-testid="videos-filter-btn" onClick={() => fetchContents(1)} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm h-9 px-4 text-xs font-semibold">Filter</Button>
      </div>

      {/* Stats bar */}
      {selectedProject && (
        <div className="flex items-center gap-4 text-xs text-[#A1A1AA]">
          <span>{contents.total} videos total</span>
          <span>Page {contents.page} / {contents.pages}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#2A2A2E]">
              <th className="text-left text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3 w-12"></th>
              <th className="text-left text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Title</th>
              <th className="text-center text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3 hidden md:table-cell">Duration</th>
              <th className="text-center text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3 hidden lg:table-cell">Sources</th>
              <th className="text-center text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3 hidden lg:table-cell">Image</th>
              <th className="text-center text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Status</th>
              <th className="text-right text-[10px] tracking-[0.15em] uppercase text-[#A1A1AA] font-bold px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A2A2E]">
            {!selectedProject ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-sm text-[#555]">Select a project to view videos</td></tr>
            ) : loading ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-sm text-[#555]">Loading...</td></tr>
            ) : contents.items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-sm text-[#555]">No videos found</td></tr>
            ) : contents.items.map(item => (
              <tr key={item.id} className={`hover:bg-[#1A1A1E] transition-colors ${!hasImage(item) ? 'opacity-50' : ''}`}>
                <td className="px-4 py-2">
                  {hasImage(item) ? (
                    <img src={item.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded-sm bg-[#0A0A0C]" />
                  ) : (
                    <div className="w-16 h-10 bg-[#0A0A0C] border border-[#2A2A2E] rounded-sm flex items-center justify-center"><Image size={14} className="text-[#555]" /></div>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="text-sm text-white font-medium truncate max-w-xs">{item.title}</div>
                  {item.translated_title && <div className="text-xs text-[#4361EE] truncate max-w-xs">{item.translated_title}</div>}
                  <div className="text-[10px] text-[#555]">{(item.tags || []).slice(0, 3).join(", ")}{item.tags?.length > 3 ? ` +${item.tags.length - 3}` : ""}</div>
                </td>
                <td className="px-4 py-2 text-center text-xs text-[#A1A1AA] hidden md:table-cell">{item.duration || "-"}</td>
                <td className="px-4 py-2 text-center hidden lg:table-cell">
                  <span className={`text-xs font-bold ${(item.video_sources || []).length > 0 ? 'text-emerald-400' : 'text-[#555]'}`}>
                    {(item.video_sources || []).length}
                  </span>
                </td>
                <td className="px-4 py-2 text-center hidden lg:table-cell">
                  {hasImage(item) ? <Check size={14} className="inline text-emerald-400" /> : <X size={14} className="inline text-[#E53935]" />}
                </td>
                <td className="px-4 py-2 text-center">
                  <button data-testid={`toggle-status-${item.id}`} onClick={() => toggleStatus(item)} className="group" title={item.status === "published" ? "Click to deactivate" : "Click to activate"}>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold cursor-pointer transition-all ${item.status === 'published' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 group-hover:bg-[#E53935]/10 group-hover:text-[#E53935] group-hover:border-[#E53935]/20' : 'bg-[#2A2A2E] text-[#555] group-hover:bg-emerald-500/10 group-hover:text-emerald-400 group-hover:border-emerald-500/20'}`}>
                      {item.status === "published" ? "active" : "inactive"}
                    </span>
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-video-${item.id}`} onClick={() => openEdit(item)} className="p-1.5 text-[#A1A1AA] hover:text-[#4361EE] transition-colors" title="Edit"><PencilSimple size={14} /></button>
                    <button data-testid={`delete-video-${item.id}`} onClick={() => handleDelete(item)} className="p-1.5 text-[#A1A1AA] hover:text-[#E53935] transition-colors" title="Delete"><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {contents.pages > 1 && (
        <div className="flex justify-center gap-2">
          {contents.page > 1 && <button onClick={() => fetchContents(contents.page - 1)} className="px-3 py-1 text-sm rounded-sm bg-[#141417] text-[#A1A1AA] border border-[#2A2A2E] hover:border-[#3A3A3E]">Prev</button>}
          {Array.from({ length: Math.min(contents.pages, 10) }, (_, i) => {
            const p = contents.pages <= 10 ? i + 1 : Math.max(1, contents.page - 4) + i;
            if (p > contents.pages) return null;
            return <button key={p} onClick={() => fetchContents(p)} className={`px-3 py-1 text-sm rounded-sm transition-colors ${contents.page === p ? 'bg-[#E53935] text-white' : 'bg-[#141417] text-[#A1A1AA] border border-[#2A2A2E] hover:border-[#3A3A3E]'}`}>{p}</button>;
          })}
          {contents.page < contents.pages && <button onClick={() => fetchContents(contents.page + 1)} className="px-3 py-1 text-sm rounded-sm bg-[#141417] text-[#A1A1AA] border border-[#2A2A2E] hover:border-[#3A3A3E]">Next</button>}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-[#141417] border-[#2A2A2E] text-white max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Edit Video</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3 mt-2">
              {editItem.thumbnail_url && <img src={editItem.thumbnail_url} alt="" className="w-full h-40 object-cover rounded-sm bg-[#0A0A0C]" />}
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Title</label><Input data-testid="edit-title" value={editItem.title || ""} onChange={e => setEditItem({...editItem, title: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-10" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Description</label><textarea data-testid="edit-desc" value={editItem.description || ""} onChange={e => setEditItem({...editItem, description: e.target.value})} className="w-full bg-[#0A0A0C] border border-[#2A2A2E] text-white rounded-sm p-2 h-24 text-sm resize-y" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Thumbnail URL</label><Input value={editItem.thumbnail_url || ""} onChange={e => setEditItem({...editItem, thumbnail_url: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs" /></div>
                <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Duration</label><Input value={editItem.duration || ""} onChange={e => setEditItem({...editItem, duration: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs" /></div>
              </div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Tags (comma separated)</label><Input data-testid="edit-tags" value={editItem.tags_str || ""} onChange={e => setEditItem({...editItem, tags_str: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs" /></div>
              <div><label className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold mb-1 block">Categories (comma separated)</label><Input value={editItem.categories_str || ""} onChange={e => setEditItem({...editItem, categories_str: e.target.value})} className="bg-[#0A0A0C] border-[#2A2A2E] text-white rounded-sm h-9 text-xs" /></div>
              <div className="bg-[#0A0A0C] border border-[#2A2A2E] rounded-sm p-3">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] font-bold mb-2">Video Sources ({(editItem.video_sources || []).length})</div>
                {(editItem.video_sources || []).map((s, i) => (
                  <div key={i} className="text-xs text-[#A1A1AA] truncate">{s.quality}: {s.url?.substring(0, 60)}...</div>
                ))}
                {(editItem.video_sources || []).length === 0 && <div className="text-xs text-[#555]">No video sources</div>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => setEditDialog(false)} className="text-[#A1A1AA] hover:text-white rounded-sm">Cancel</Button>
                <Button data-testid="save-edit-btn" onClick={handleSaveEdit} className="bg-[#E53935] hover:bg-[#C62828] text-white rounded-sm px-6 font-semibold">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
